<template>
    <div class="apprentice-simple-card">
        <design-2021-apprentice-data-table
            class="slu-course-cohorts"
            :items="tableData"
            :fields="cohortFields">
            <template
                v-slot:options
                v-if="$authUser.hasRoleOrAdmin('slu')">
                <b-button
                    class="btn-add btn-add-slu-cohort"
                    v-b-modal="'add-slu-cohort-modal'"
                    type="button"
                    label="create-new-slu-cohort"
                    variant="primary">
                    {{ $t('slu_dashboard.add_grade') }}
                </b-button>
                <b-button
                    class="btn-add btn-copy-slu-cohort"
                    v-b-modal="'copy-slu-cohort-modal'"
                    type="button"
                    label="copy-slu-cohort"
                    variant="primary">
                    {{ $t('slu_dashboard.copy_grade') }}
                </b-button>
                <b-button
                    class="btn-images btn-change-banner-image"
                    v-b-modal="'change-slu-course-banner-image-modal'"
                    type="button"
                    label="change-banner-image"
                    variant="primary">
                    {{ $t('slu_dashboard.change_banner') }}
                </b-button>
            </template>
            <template v-slot:cell(progress)="data">
                <b-progress :max="data.item.totalHours + data.item.completedBonusHours">
                    <b-progress-bar
                        v-b-tooltip.hover
                        :value="data.item.completedHours"
                        :label="`${((data.item.completedHours / (data.item.totalHours + data.item.completedBonusHours)) * 100).toFixed(0)}%`"
                        :title="$t('slu_dashboard.completed_hours')"
                        variant="primary">
                    </b-progress-bar>
                    <b-progress-bar
                        v-b-tooltip.hover
                        v-if="data.item.completedBonusHours > 0"
                        :value="data.item.completedBonusHours"
                        :label="`${((data.item.completedBonusHours / (data.item.totalHours + data.item.completedBonusHours)) * 100).toFixed(0)}%`"
                        :title="$t('slu_dashboard.completed_bonus_hours')"
                        variant="success">
                    </b-progress-bar>
                </b-progress>
            </template>
            <template v-slot:cell(action)="data">
                <div class="management-actions">
                    <b-button
                        class="btn-open btn-open-slu-cohort btn-"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('common.open')"
                        @click="openSluCohort(data.item.id)">
                    </b-button>
                    <b-button
                        class="btn-edit btn-edit-slu-cohort btn-"
                        v-b-tooltip.hover
                        v-if="$authUser.hasRoleOrAdmin('slu')"
                        variant="primary-icon-only"
                        :title="$t('common.edit')"
                        @click="editSluCohort(data.item.id)">
                    </b-button>
                    <b-button
                        class="btn-remove btn-remove-slu-cohort"
                        v-if="$authUser.hasRoleOrAdmin('slu')"
                        variant="danger-icon-only"
                        @click="deleteCohort(data.item.id)">
                    </b-button>
                </div>
            </template>
        </design-2021-apprentice-data-table>
        <design-2021-add-slu-cohort-modal-component
            ref="add-slu-cohort-modal"
            :slu-course-in="sluCourse"
            :seasons-in="seasonsIn"
            :slu-school-settings-in="sluSchoolSettingsIn"
            @updateCohorts="addCohort($event)">
        </design-2021-add-slu-cohort-modal-component>
        <design-2021-copy-slu-cohort-modal-component
            ref="copy-slu-cohort-modal"
            :seasons-in="seasonsIn"
            :slu-cohorts-in="cohorts"
            @updateCohorts="addCohort($event)">
        </design-2021-copy-slu-cohort-modal-component>
        <design-2021-update-slu-cohort-modal-component
            :slu-course-in="sluCourse"
            :seasons-in="seasonsIn"
            :slu-school-settings-in="sluSchoolSettingsIn"
            @updateCohorts="updateCohort($event)"
            ref="edit-slu-cohort-modal">
        </design-2021-update-slu-cohort-modal-component>
        <design-2021-slu-course-banner-upload-modal-component
            :slu-course-in="sluCourse"
            ref="change-slu-course-banner-image-modal">
        </design-2021-slu-course-banner-upload-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-slu-dashboard-course-index-component',
    props: ['sluCourseIn', 'seasonsIn', 'sluSchoolSettingsIn'],
    data() {
        return {
            sluCourse: this.sluCourseIn,
            cohorts: this.sluCourseIn.cohorts,
        };
    },
    computed: {
        cohortFields() {
            let fields = [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'season',
                    label: this.$t('slu_dashboard.season'),
                },
            ];
            if (this.sluSchoolSettingsIn.stc_synq) {
                fields.push({
                    key: 'stc_course_uuid',
                    label: 'Uuid',
                });
            }
            fields = fields.concat([
                {
                    key: 'progress',
                    label: this.$t('slu_dashboard.progress'),
                },
                {
                    key: 'action',
                    label: this.$t('common.actions'),
                },
            ]);
            return fields;
        },
        tableData() {
            return this.cohorts.map((cohort) => {
                let totalHours = 0;
                if (cohort.modules) {
                    totalHours =
                        cohort.modules
                            .map((module) => {
                                return module.required ? module.hours : 0;
                            })
                            .reduce((partialSum, a) => partialSum + a, 0) * cohort.users.length;
                }
                let completedHours = 0;
                let completedBonusHours = 0;
                if (cohort.users) {
                    cohort.users.forEach((user) => {
                        cohort.modules.forEach((module) => {
                            const moduleRecord = module.slu_module_records.find((record) => record.user_id === user.id);
                            if (moduleRecord) {
                                if (moduleRecord.completed) {
                                    if (module.required) {
                                        completedHours += module.hours;
                                    } else {
                                        completedBonusHours += module.hours;
                                    }
                                }
                            }
                        });
                    });
                }

                return {
                    id: cohort.id,
                    name: cohort.name,
                    season: cohort.season.season,
                    stc_course_uuid: cohort.stc_course_uuid,
                    totalHours: totalHours,
                    completedHours: completedHours,
                    completedBonusHours: completedBonusHours,
                };
            });
        },
    },
    methods: {
        openSluCohort(cohortId) {
            window.location.href = this.routes.sluCohortGet(this.sluCourse.id, cohortId);
        },
        updateCohort(data) {
            this.cohorts = this.cohorts.map((cohort) => {
                if (cohort.id === data.id) {
                    const newCohort = { ...cohort, ...data };
                    return newCohort;
                } else {
                    return cohort;
                }
            });
        },
        editSluCohort(cohortId) {
            const cohort = this.cohorts.find((cohort) => cohort.id === cohortId);
            this.$refs['edit-slu-cohort-modal'].show(cohort);
        },
        addCohort(cohort) {
            this.cohorts.push(cohort);
        },

        deleteCohort(id) {
            this.alert
                .confirm(
                    this.$t('slu_dashboard.delete_grade'),
                    this.$t('slu_dashboard.are_you_sure_you_want_to_delete_this_grade'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios.delete(this.routes.sluCohort(this.sluCourse.id), { data: { sluCohortId: id } }).then(
                        (response) => {
                            this.cohorts = this.cohorts.filter((item) => item.id !== id);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
    },
};
</script>

<style scoped lang="scss">
.slu-course-cohorts ::v-deep {
    .options {
        margin-left: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .btn-change-banner-image,
    .btn-copy-slu-cohort {
        margin-left: map-get($grid-gutter-spacing, 'fifteen');
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .slu-course-cohorts ::v-deep {
        .apprentice-table-header {
            flex-direction: row;
        }

        .apprentice-table-filters {
            margin-bottom: 0;
            flex: 0 0 35%;
        }

        .options {
            flex: 0 0 auto;
        }
    }
}
</style>
